import { useEffect, useState } from "react";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CircularProgress,
  Alert,
  Snackbar,
  Chip,
  Grid,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from "@mui/material";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import properties from "constants/ApplicationConstants";
import { GET, PATCH } from "APIclient";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";

function ListTechnicalSkills() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [skills, setSkills] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [activeSearchTerm, setActiveSearchTerm] = useState("");
  const [activeStatusFilter, setActiveStatusFilter] = useState("active");
  const [notification, setNotification] = useState({ open: false, message: "", type: "success" });
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

  useEffect(() => {
    fetchSkills();
  }, []);

  const fetchSkills = async () => {
    try {
      setLoading(true);
      const empId = localStorage.getItem("empId") || sessionStorage.getItem("empId");
      if (!empId) {
        showNotification("Employee ID not found", "error");
        setLoading(false);
        return;
      }

      const response = await GET(`manager/list-kraSkills/${empId}`);
      if (response.data?.data) {
        const technicalPrimarySkills = response.data.data.filter(
          skill => skill.skillTypes.toLowerCase().replace(/[-\s]/g, "") === "technicalprimary"
        );
        setSkills(
          technicalPrimarySkills.map((skill, index) => ({
            ...skill,
            id: skill.paFormId,
            serialNo: index + 1
          }))
        );
      } else {
        setSkills([]);
      }
    } catch (error) {
      showNotification("Failed to load skills", "error");
      setSkills([]);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (paFormId, currentStatus) => {
    try {
      const newStatus = currentStatus === 1 ? 0 : 1;
      const response = await PATCH(`manager/skills/${paFormId}/status?status=${newStatus}`, null, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("jwtToken") || sessionStorage.getItem("jwtToken")}`
        }
      });
      if (response.status === 200) {
        showNotification(newStatus === 1 ? "Skill activated successfully" : "Skill deactivated successfully", "success");
        fetchSkills();
      }
    } catch (error) {
      showNotification("Failed to update skill status", "error");
    }
  };

  const showNotification = (message, type) => {
    setNotification({ open: true, message, type });
    setTimeout(() => setNotification({ open: false, message: "", type: "success" }), 4000);
  };

  const handleSearch = () => {
    setActiveSearchTerm(searchTerm);
    setActiveStatusFilter(statusFilter);
  };

  const handleReset = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setActiveSearchTerm("");
    setActiveStatusFilter("active");
    setPaginationModel({ page: 0, pageSize: 10 });
  };

  const getStatusChip = status =>
    status === 1 ? (
      <Chip
        label="Active"
        size="small"
        sx={{ backgroundColor: theme => theme.palette.success.main, color: "#fff", fontWeight: 500, height: "22px", fontSize: "12px" }}
      />
    ) : (
      <Chip
        label="In-Active"
        size="small"
        sx={{ backgroundColor: theme => theme.palette.error.main, color: "#fff", fontWeight: 500, height: "22px", fontSize: "12px" }}
      />
    );

  const formatSkillType = skillType => {
    if (!skillType) return "N/A";
    return skillType.split("-").map(word => word.charAt(0).toUpperCase() + word.slice(1)).join("-");
  };

  const filteredSkills = skills
    .filter(skill => {
      const matchesSearch = skill.skillSet.toLowerCase().includes(activeSearchTerm.toLowerCase());
      const matchesStatus =
        activeStatusFilter === "active" ? skill.status === 1 :
          activeStatusFilter === "inactive" ? skill.status === 0 : true;
      return matchesSearch && matchesStatus;
    })
    .map((skill, index) => ({ ...skill, serialNo: index + 1 }));

  const columns = [
    { field: "serialNo", headerName: "#", width: 65, headerAlign: "center", align: "center" },
    { field: "skillSet", headerName: "Skill Set", width: 300, flex: 1 },
    { field: "skillTypes", headerName: "Skill Type", width: 190, renderCell: params => formatSkillType(params.value) },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      headerAlign: "center",
      align: "center",
      renderCell: params => getStatusChip(params.value)
    },
    {
      field: "actions",
      headerName: "Action",
      width: 110,
      headerAlign: "center",
      align: "center",
      type: "actions",
      getActions: params => {
        const isActive = params.row.status === 1;
        return [
          <Tooltip title={isActive ? "Deactivate Skill" : "Activate Skill"} arrow key="toggle">
            <GridActionsCellItem
              icon={isActive ? <CancelIcon /> : <CheckCircleIcon />}
              label={isActive ? "Deactivate" : "Activate"}
              color={isActive ? "error" : "success"}
              onClick={() => handleToggleStatus(params.row.paFormId, params.row.status)}
            />
          </Tooltip>
        ];
      }
    }
  ];

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress size={32} />
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={0.5} pb={1.5}>
        <Card>
          {/* ── Header ── */}
          <MDBox
            variant="gradient"
            sx={{ background: properties.colors.bgcolorbluethem }}
            borderRadius="lg"
            coloredShadow="success"
            mx={1.5}
            mt={0.5}
            p={1}
            textAlign="center"
          >
            <MDTypography
              variant="h6"
              fontWeight="600"
              color="white"
              sx={{
                fontFamily: properties.fontFamily,
                mb: 0,          // remove bottom margin
                lineHeight: 1.2 // tighter spacing
              }}
            >
              Technical Skills
            </MDTypography>

            <MDTypography
              variant="caption"
              color="white"
              opacity={0.8}
              sx={{
                fontFamily: properties.fontFamily,
                mt: 0,          // remove top margin
                lineHeight: 1.2
              }}
            >
              View and manage technical primary skills
            </MDTypography>
          </MDBox>
          
          <MDBox p={1.5}>
            {/* ── Search Section ── */}
            <Card sx={{ px: 1.5, py: 1, mb: 1.5, backgroundColor: "#f8f9fa" }}>
              <MDTypography
                variant="subtitle2"
                fontWeight="600"
                mb={1}
                sx={{ fontFamily: properties.fontFamily }}
              >
                Search Skills
              </MDTypography>
              <Grid container spacing={1} alignItems="flex-end">
                <Grid item xs={12} md={4}>
                  <MDInput
                    fullWidth
                    type="text"
                    label="Skill Name"
                    variant="standard"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    onKeyPress={e => e.key === "Enter" && handleSearch()}
                    placeholder="Search by skill name"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth variant="standard">
                    <InputLabel id="status-filter-label">Status</InputLabel>
                    <Select
                      labelId="status-filter-label"
                      value={statusFilter}
                      label="Status"
                      onChange={e => setStatusFilter(e.target.value)}
                      sx={{ fontFamily: properties.fontFamily }}
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="inactive">In-Active</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={5}>
                  <MDBox display="flex" gap={0.75}>
                    <MDButton
                      variant="gradient"
                      color="info"
                      size="small"
                      startIcon={<SearchIcon />}
                      onClick={handleSearch}
                      sx={{ background: properties.colors.bgcolorbluethem, fontFamily: properties.fontFamily, height: "32px" }}
                    >
                      Search
                    </MDButton>
                    <MDButton
                      variant="outlined"
                      color="secondary"
                      size="small"
                      startIcon={<RefreshIcon />}
                      onClick={handleReset}
                      sx={{ fontFamily: properties.fontFamily, height: "32px" }}
                    >
                      Reset
                    </MDButton>
                  </MDBox>
                </Grid>
              </Grid>
            </Card>

            {/* ── DataGrid ── */}
            <DataGrid
              rows={filteredSkills}
              columns={columns}
              pagination
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[5, 10, 25, 50]}
              disableRowSelectionOnClick
              autoHeight
              rowHeight={40}
              columnHeaderHeight={42}
              sx={{
                fontFamily: properties.fontFamily,
                fontSize: "13px",
                fontWeight: 400,
                "& .MuiDataGrid-scrollbar": { width: "8px !important" },
                "& .MuiDataGrid-columnHeaders": { backgroundColor: "#f5f5f5" },
                "& .MuiDataGrid-cell": { py: 0 }
              }}
            />

            <MDBox mt={1} display="flex" justifyContent="space-between" alignItems="center">
              <MDTypography variant="caption" color="text">
                Showing {filteredSkills.length} technical primary skills
              </MDTypography>
            </MDBox>
          </MDBox>
        </Card>

        {/* ── Notification ── */}
        <Snackbar
          open={notification.open}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          autoHideDuration={4000}
          onClose={() => setNotification({ ...notification, open: false })}
        >
          <Alert
            onClose={() => setNotification({ ...notification, open: false })}
            severity={notification.type}
            variant="filled"
            sx={{ width: "100%", color: "#fff" }}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      </MDBox>
    </DashboardLayout>
  );
}

export default ListTechnicalSkills;